import type { AiModel, AiModelInput } from "./types";

/**
 * Everything the model form computes, with no React in it.
 *
 * Extracted when the form left its modal for a page of its own. Two components
 * now need this arithmetic — the form to show a margin as somebody types, the
 * list to colour one in a column — and the alternative to a shared module was
 * one page importing helpers from the other, which is how a list page ends up
 * unable to render without the editor loaded.
 *
 * The pricing functions mirror the API's. If the two ever disagree, the API is
 * right: it is what actually charges, and this only draws a preview.
 */

const MICROS_PER_UNIT = 1_000_000;

export const EMPTY_MODEL_FORM: AiModelInput = {
  id: "",
  upstreamId: "",
  provider: "openai",
  displayName: "",
  contextWindow: null,
  maxOutputTokens: null,
  officialInputMicros: 0,
  officialOutputMicros: 0,
  officialVerifiedAt: null,
  markupBps: null,
  // 1.00x. A model nobody has measured is assumed to cost what it lists at,
  // which is the only assumption that cannot price it under cost.
  costFactorBps: 10_000,
  available: false,
  zdr: false,
  capabilities: [],
  description: "",
  featuredRank: null,
};

/**
 * Publisher prefixes offered when the catalogue cannot answer.
 *
 * Reached only before a first sync. Normally the form and the filter both offer
 * the publishers actually present, read from the list response — which is the fix
 * for how this constant went wrong: it named six, one of which (`workers-ai`) is
 * retired and two of which (`grok`, `groq`) were never the supplier's spelling,
 * against a catalogue holding fifty-nine. Two thirds of it could not be filtered
 * to at all.
 */
export const FALLBACK_PUBLISHERS = [
  "openai",
  "anthropic",
  "google",
  "meta-llama",
  "mistralai",
  "deepseek",
  "qwen",
  "x-ai",
];

/** Micros per million tokens to the decimal string the rate fields show. */
export function toRate(micros: number): string {
  return (micros / MICROS_PER_UNIT).toFixed(6);
}

export function parseRate(raw: string): number | null {
  const parsed = Number(raw);
  if (raw.trim() === "" || !Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * MICROS_PER_UNIT);
}

/** Mirrors inferenceCostMicros on the API. If they disagree, the API is right. */
export function costMicros(official: number, costFactorBps = 10_000): number {
  return Math.ceil((official * costFactorBps * 1_055) / (10_000 * 1_000));
}

/** Mirrors inferenceSellMicros. Derived here only so the form can show it live. */
export function sellMicros(official: number, markupBps: number, costFactorBps = 10_000): number {
  return Math.ceil((costMicros(official, costFactorBps) * (10_000 + markupBps)) / 10_000);
}

export function marginBps(official: number, sell: number, costFactorBps = 10_000): number {
  const cost = costMicros(official, costFactorBps);
  if (cost === 0) return 0;
  return Math.round(((sell - cost) * 10_000) / cost);
}

export function percent(bps: number): string {
  return `${(bps / 100).toFixed(1)}%`;
}

/* ---------------------------------------------------- the other price basis */

/**
 * How a unit reads in a price cell. Megapixel is abbreviated; the rest are words.
 *
 * The same table, under the same name, exists on both customer surfaces. An
 * operator who reads "$0.07 / megapixel" here and hears "$0.07 / MP" quoted back
 * from the catalogue is comparing two spellings of one figure and has to work
 * out that they match, which is exactly the kind of small friction that ends in
 * somebody checking the database.
 */
const UNIT_LABELS: Record<string, string> = {
  image: "image",
  token: "token",
  megapixel: "MP",
  second: "second",
  generation: "generation",
  "M tokens": "1M tokens",
};

/**
 * One of a generation model's per-unit prices, formatted.
 *
 * The console printed these as `${line.usd} / ${line.unit}` behind a hardcoded
 * dollar sign, which is three separate ways to disagree with the two customer
 * surfaces: `0.5` where they show `$0.50`, `megapixel` where they show `MP`, and
 * a currency symbol asserted rather than derived. The figures are the same
 * figures — an operator setting a markup against them should be reading the same
 * string a customer does.
 *
 * `usd` is always dollars, converted by the API before it reaches any client, so
 * the currency here is the catalogue's display currency rather than a second
 * conversion.
 */
export function formatPriceLine(
  line: { unit: string; usd: number },
  currency = "USD",
): string {
  return `${formatPriceValue(line.usd, currency)} / ${UNIT_LABELS[line.unit] ?? line.unit}`;
}

/** A figure on its own. The unit is carried by the row's label instead. */
export function formatPriceValue(usd: number, currency = "USD"): string {
  const trimmed = String(usd).replace(/(\.\d{4}?)0+$/, "$1").replace(/\.$/, "");

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(Number(trimmed));
  } catch {
    return `${trimmed} ${currency}`;
  }
}

/**
 * How one price line reads as a row label.
 *
 * The same two rules the customer surfaces use, and deliberately so: an operator
 * setting a markup should be reading the strings a customer reads, not a second
 * spelling of them. Same unit across the lines → the *kind* distinguishes them
 * and the unit is said once; different units → the unit is the distinction. A
 * resolution beats both, because two "per second" video lines are told apart by
 * nothing else.
 */
export function priceRowLabel(
  line: { billable: string; unit: string },
  sharedUnit: boolean,
): string {
  const resolution = /(\d{3,4}p)/.exec(line.billable)?.[1];
  if (resolution) return resolution;

  if (sharedUnit) {
    const kind = line.billable.replace(/^(input|output)_/, "").replace(/_/g, " ");
    if (kind && kind !== line.unit) return kind;
  }

  return UNIT_LABELS[line.unit] ?? line.unit;
}

/**
 * What the provider charges, as label→figure rows the Official column can stack.
 *
 * The column had two shapes glued into one: `$0.301593 / $2.326582` for a rate
 * card and `$0.035 / megapixel` repeated for a generation model. The second is
 * unreadable in a dense table — the unit is inside the value, so nothing lines
 * up and two rows of it are a paragraph.
 *
 * Splitting the label from the figure is what lets both stack in the same cell
 * with the numbers in one column, which is the comparison this page is for.
 */
export function officialRows(model: {
  billingMode?: "token" | "upstream_cost";
  official: { input: string; output: string };
  priceLines?: { billable: string; unit: string; usd: number }[];
  currency?: string;
}): { rows: { label: string; value: string }[]; caption: string | null; andMore: number } {
  if (model.billingMode !== "upstream_cost") {
    return {
      rows: [
        { label: "in", value: model.official.input },
        { label: "out", value: model.official.output },
      ],
      caption: null,
      andMore: 0,
    };
  }

  const lines = model.priceLines ?? [];
  if (lines.length === 0) return { rows: [], caption: null, andMore: 0 };

  const shown = lines.slice(0, 2);
  const sharedUnit = new Set(lines.map((line) => line.unit)).size === 1;

  return {
    rows: shown.map((line) => ({
      label: priceRowLabel(line, sharedUnit),
      value: formatPriceValue(line.usd, model.currency ?? "USD"),
    })),
    caption: sharedUnit ? (UNIT_LABELS[lines[0]!.unit] ?? lines[0]!.unit) : null,
    andMore: Math.max(0, lines.length - shown.length),
  };
}

export function isFeaturedRank(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

export function setFormAvailability(form: AiModelInput, available: boolean): AiModelInput {
  return available ? { ...form, available } : { ...form, available, featuredRank: null };
}

/**
 * The edit form's starting values for an existing model.
 *
 * Carries `upstreamId` and deliberately not `id`. The routing id is editable —
 * a provider renaming a model upstream is a correction, and delete-and-recreate
 * would take it off sale and orphan its usage history to fix a string. The public
 * id is the primary key every usage row references, so changing it is a
 * migration rather than a form field.
 */
/**
 * A date the browser's date input will actually display.
 *
 * `input type="date"` accepts `YYYY-MM-DD` and nothing else. Give it
 * `"2026-08-23T00:00:00.000Z"` and it does not show a wrong date, it shows *no*
 * date — so an operator opening a model that has a verification date sees an
 * empty field, and saving the form would then clear a value nobody meant to
 * touch.
 *
 * The API sends the short form now. This stays because a console deployed ahead
 * of the API would otherwise show every one of those fields blank, and because
 * the cost of tolerating both shapes is one `slice`.
 *
 * Sliced rather than parsed. `new Date(day).getDate()` reads a UTC-midnight value
 * through the local calendar and returns the previous day anywhere west of
 * Greenwich, which is a date that is wrong rather than missing — the harder of
 * the two to notice.
 */
function calendarDay(value: string | null): string | null {
  return value ? value.slice(0, 10) : null;
}

export function editFormFor(model: AiModel): AiModelInput {
  return {
    upstreamId: model.upstreamId,
    displayName: model.displayName,
    contextWindow: model.contextWindow,
    maxOutputTokens: model.maxOutputTokens,
    officialInputMicros: model.official.inputMicrosPerMTok,
    officialOutputMicros: model.official.outputMicrosPerMTok,
    officialVerifiedAt: calendarDay(model.officialVerifiedAt),
    markupBps: model.ownMarkupBps,
    costFactorBps: model.costFactorBps,
    available: model.available,
    zdr: model.zdr,
    capabilities: model.capabilities,
    description: model.description,
    featuredRank: isFeaturedRank(model.featuredRank) ? model.featuredRank : null,
  };
}

/**
 * Where the editor for a model lives.
 *
 * A splat route rather than a `:modelId` param, because a public id carries a
 * slash — `meta/llama-3.2-1b-instruct` is two path segments — and percent-encoding
 * it into one is a fight with the browser, the router and any proxy in between.
 * The API solved the same problem the same way, with `:modelId{.+}`.
 *
 * Not encoded here for that reason: the id goes into the path as the segments it
 * already is, and the splat hands back exactly what was put in.
 */
export function modelEditPath(id: string): string {
  return `/ai-models/edit/${id}`;
}

/** How long a model wears the "new" mark in the console. */
export const NEW_MODEL_WINDOW_DAYS = 7;

/**
 * Whether the supplier published this model within that window.
 *
 * Reads `upstreamCreatedAt` — the supplier's publication date — falling back to
 * `catalogueDiscoveredAt` only where no scan ever recorded one. That pair is
 * exactly what the public catalogue resolves its `created` field to, and it is
 * spelled out here rather than approximated because the console gets the same
 * mark as the marketplace on purpose: an operator fielding "is this model new?"
 * should be reading the customer's answer, not a different one derived from a
 * different column.
 *
 * It read `catalogueDiscoveredAt` alone until that was shown to mark everything.
 * The catalogue was seeded over five days in August 2026, 328 of the 392 listed
 * rows in one run, so every discovery date fell inside this seven-day window
 * together and the badge landed on the whole table at once.
 *
 * The dates are not identical — they are milliseconds apart — so this never
 * looked like a tie. It looked like a table where every row was new, which is
 * the same failure wearing a more convincing face.
 *
 * The console sees unlisted rows the marketplace does not, and the mark is more
 * useful for it: a model the supplier released this week and nobody has listed
 * yet is the one worth pricing, and it now says so.
 *
 * `now` is a parameter so a test can pin it. A date that is absent, unreadable
 * or in the future is not new — a future one would otherwise stick permanently.
 */
export function isNewModel(
  model:
    | { upstreamCreatedAt?: string | null; catalogueDiscoveredAt?: string | null }
    | null
    | undefined,
  now = Date.now(),
): boolean {
  const published = Date.parse(model?.upstreamCreatedAt ?? model?.catalogueDiscoveredAt ?? "");
  if (!Number.isFinite(published)) return false;

  const age = now - published;
  return age >= 0 && age < NEW_MODEL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}
